import deepmerge from 'deepmerge';

const defaultBorder = {
  borderColor: 'transparent',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: 0
};

export const defaultAction = {
  action1: {
    // clone defaultBorder to avoid accidental shared mutation
    default: { ...defaultBorder }
  },
  action2: {
    default: { ...defaultBorder }
  },
  action3: {
    default: { ...defaultBorder }
  }
};

const createPalette = ({
  type,
  blue500,
  blue600,
  blue700,
  grey40,
  grey60,
  grey80,
  grey200,
  grey300
}: any) => {
  if (type === 'light') {
    return {
      color1: blue500,
      color2: blue600,
      color3: blue700,
      color4: grey40,
      color5: grey60,
      color6: grey200
    };
  }
  return {
    color1: blue500,
    color2: blue600,
    color3: blue700,
    color4: grey200,
    color5: grey300,
    color6: grey80
  };
};

const createAction1 = (palette: any, {
  typography
}: any) => {
  const { getTypography } = typography;
  const { color1, color2, color3 } = palette;
  const createActionDensity = (density: any) => {
    const {
      color: primaryActionColor,
      minHeight: unusedMinHeight,
      ...primaryActionRest
    } = getTypography({
      variant: 'primaryAction',
      density
    });
    return {
      default: {
        ...defaultAction.action1.default,
        background: color2,
        foreground: primaryActionColor,
        icon: primaryActionColor,
        ...primaryActionRest
      },
      hover: {
        ...defaultBorder,
        background: color1,
        foreground: primaryActionColor,
        icon: primaryActionColor
      },
      active: {
        ...defaultBorder,
        background: color3,
        foreground: primaryActionColor,
        icon: primaryActionColor
      }
    };
  };
  return {
    touch: createActionDensity('touch'),
    low: createActionDensity('low'),
    medium: createActionDensity('medium'),
    high: createActionDensity('high')
  };
};

const createAction2 = (palette: any, {
  typography
}: any) => {
  const { getTypography } = typography;
  const { color4, color5, color6 } = palette;
  const createActionDensity = (density: any) => {
    const {
      color: actionColor,
      minHeight: unusedMinHeight,
      ...actionRest
    } = getTypography({
      variant: 'action',
      density
    });
    const {
      color: actionInvertedColor,
      minHeight: unusedInvertedMinHeight,
      ...actionInvertedRest
    } = getTypography({
      variant: 'actionInverted',
      density
    });
    return {
      default: {
        ...defaultAction.action2.default,
        background: color5,
        foreground: actionColor,
        icon: actionColor,
        ...actionRest
      },
      hover: {
        ...defaultBorder,
        background: color4,
        foreground: actionColor,
        icon: actionColor
      },
      active: {
        background: color6,
        foreground: actionInvertedColor,
        icon: actionInvertedColor,
        ...actionInvertedRest
      }
    };
  };
  return {
    touch: createActionDensity('touch'),
    low: createActionDensity('low'),
    medium: createActionDensity('medium'),
    high: createActionDensity('high')
  };
};

const createAction3 = (palette: any, {
  iconography,
  typography
}: any) => {
  const { getTypography } = typography;
  const { color4, color6 } = palette;
  const createActionDensity = (density: any) => {
    const {
      color: actionColor,
      minHeight: unusedMinHeight,
      ...actionRest
    } = getTypography({
      variant: 'action',
      density
    });
    const {
      color: actionInvertedColor,
      minHeight: unusedInvertedMinHeight,
      ...actionInvertedRest
    } = getTypography({
      variant: 'actionInverted',
      density
    });
    const { icon, iconInverted } = iconography;
    return {
      default: {
        ...defaultAction.action3.default,
        background: 'none',
        foreground: actionColor,
        icon,
        ...actionRest
      },
      hover: {
        background: color4,
        foreground: actionColor,
        icon: icon.color
      },
      active: {
        background: color6,
        foreground: actionInvertedColor,
        icon: iconInverted.color,
        ...actionInvertedRest
      }
    };
  };
  return {
    touch: createActionDensity('touch'),
    low: createActionDensity('low'),
    medium: createActionDensity('medium'),
    high: createActionDensity('high')
  };
};

const createGetAction = (options: any) => ({
  variant = 'action1',
  density = 'medium'
} = {}) => {
  const { hasOwnProperty } = Object.prototype;
  if (
    !hasOwnProperty.call(options, variant)
    || !hasOwnProperty.call(options[variant], density)
  ) {
    return {};
  }
  return options[variant][density];
};

// noinspection JSAnnotator
export const createAction = (palettes: any, action = {}) => {
  const { getAction: otherGetAction, ...otherOptions }
  = typeof action === 'function' ? action(palettes) : action;
  const actionPalette = createPalette(palettes.palette);
  const options = {
    action1: createAction1(actionPalette, palettes),
    action2: createAction2(actionPalette, palettes),
    action3: createAction3(actionPalette, palettes)
  };
  const mergedOptions = deepmerge(options, otherOptions, {
    clone: false
  });
  return {
    getAction: otherGetAction || createGetAction(mergedOptions),
    ...mergedOptions
  };
};
